<template>
	<slot></slot>

    <div v-if="!state.locations_loaded" class="px-4 py-5 sm:p-6">
        <LoadingSection />
    </div>

    <fieldset v-if="state.locations_loaded" class="mb-12">
        <div class="space-y-4 mb-8">
            <label v-for="(location, index) in state.locations" :key="index" class="group relative block cursor-pointer rounded-lg border bg-white pr-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between hover:border-blue-500 transition" :class="state.current_location_id == location.id ? 'border-transparent border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'">
                <input v-model="state.current_location_id" type="radio" name="server-size" :value="location.id" class="sr-only" :aria-labelledby="`location-${location.id}-name`" :aria-describedby="`location-${location.id}-description`">
                <div class="flex-shrink-0 px-3 flex items-center justify-center">
                    <span class="transition rounded-full h-6 w-6 flex items-center justify-center group-hover:border-blue-500" :class="state.current_location_id == location.id ? 'bg-blue-500' : 'border border-2 border-gray-200'">
                        <CheckIcon v-show="state.current_location_id == location.id" class="text-white h-5 w-5" />
                    </span>
                </div>
                <span class="flex-1 items-center">
                    <span class="flex flex-col text-sm">
                        <span :id="`location-${location.id}-name`" class="font-medium text-gray-900" v-html="location.name"></span>
                        <span :id="`location-${location.id}-description`" class="text-gray-500">
                            <span class="block sm:inline" v-html="location.address_line_1"></span>
                            <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                            <span class="block sm:inline" v-html="location.town"></span>
                            <span class="hidden sm:mx-1 sm:inline" aria-hidden="true">&middot;</span>
                            <span class="block sm:inline" v-html="location.postcode"></span>
                        </span>
                    </span>
                </span>
                <span class="pointer-events-none absolute -inset-px rounded-lg border-1" :class="state.current_location_id == location.id ? 'border border-blue-500' : 'border-1 border-transparent'" aria-hidden="true"></span>
            </label>
            <label class="group relative block cursor-pointer rounded-lg border bg-white pr-6 py-4 shadow-sm focus:outline-none sm:flex sm:justify-between hover:border-blue-500 transition" :class="state.current_location_id == 'OTHER' ? 'border-transparent border-blue-500 ring-1 ring-blue-500' : 'border-gray-300'">
                <input v-model="state.current_location_id" type="radio" name="server-size" :value="'OTHER'" class="sr-only">
                <div class="flex-shrink-0 px-3 flex items-center justify-center">
                    <span class="transition rounded-full h-6 w-6 flex items-center justify-center group-hover:border-blue-500" :class="state.current_location_id == 'OTHER' ? 'bg-blue-500' : 'border border-2 border-gray-200'">
                        <CheckIcon v-show="state.current_location_id == 'OTHER'" class="text-white h-5 w-5" />
                    </span>
                </div>
                <span class="flex-1 items-center">
                    <span class="flex flex-col text-sm">
                        <span class="font-medium text-gray-900">{{ no_location_label || 'Other' }}</span>
                    </span>
                </span>
            </label>
        </div>
    </fieldset>
</template>

<script>
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { useUserStore } from '@sto/user.store'
import * as LocationService from '@ser/location'

import CheckIcon from '@ico/CheckIcon.vue'
export default {
    props: ['asset', 'current_location_id', 'seller_account_id', 'auctioneer_id', 'buyer_account_id', 'no_location_label'],
    components: {
        CheckIcon
    },
	setup(props, {emit}) {

        const USER_STORE = useUserStore()

        const state = reactive({
            locations_loading: false,
            locations_loaded: false,
            locations: [],
            current_location_id: props.current_location_id
        })

        const user_is_seller = computed(() => {
            return props.seller_account_id ? USER_STORE.userIsSellerOfAsset({asset_seller_account_id: props.seller_account_id})
                : false
        })

        const user_is_auctioneer = computed(() => {
            return props.auctioneer_id ? USER_STORE.userIsAuctioneerOfAsset({asset_auctioneer_id: props.auctioneer_id})
                : false
        })

        const user_is_buyer = computed(() => {
            return props.buyer_account_id ? USER_STORE.userIsBuyerOfAsset({asset_buyer_account_id: props.buyer_account_id})
                : false
        })

        const setLocations = locations_data => {
            state.locations = locations_data

            if (state.current_location_id && state.current_location_id != 'OFFER') emit('new_current_location_id', state.locations.find(l => l.id == state.current_location_id))
        }

        const getLocations = async () => {

            if (state.locations_loading) return
            state.locations_loading = true

            let accounts = [],
                auctioneers = [],
                params = []

            if (user_is_seller.value) {
                accounts.push(props.seller_account_id)
            }

            if (user_is_buyer.value) {
                accounts.push(props.buyer_account_id)
            }

            if (user_is_auctioneer.value) {
                auctioneers.push(props.auctioneer_id)
            }

            if (accounts.length) params.push({key: 'account_ids', value: accounts.toString()})
            if (auctioneers.length) params.push({key: 'auctioneer_ids', value: auctioneers.toString()})

            if (!params.length) {
                return console.warn('no accounts or auctioneers')
            }

            try {
                const locations = await LocationService.find(params)
                setLocations(locations)
                
            } catch (e) {
                console.error('LocationService.find error', e)
            } finally {
                state.locations_loading = false
                state.locations_loaded = true
            }

        }

        watch(
            () => state.current_location_id,
            (new_value, old_value) => {
                if (state.current_location_id == 'OTHER') return emit('new_current_location_id', 'OTHER')

                emit('new_current_location_id', state.locations.find(l => l.id == state.current_location_id))
            }
        )

        onBeforeMount(() => {
            getLocations()
        })

        return {
            state
        }
    }
}
</script>